import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../style/Homepage.css';
import { ArrowRightIcon, CheckIcon, PauseIcon, PlayIcon } from '../components/Icons';
import capabilityAiImg from '../images/capability-ai.jpg';
import capabilityCloudImg from '../images/capability-cloud.jpg';
import capabilityEnterpriseImg from '../images/capability-enterprise.jpg';
import capabilityServicenowImg from '../images/capability-servicenow.jpg';
import capabilityCyberImg from '../images/capability-cyber.jpg';
import tileAiImg from '../images/tile-ai.jpg';
import tileCloudImg from '../images/tile-cloud.jpg';
import tileSecureImg from '../images/tile-secure.jpg';
import tileEnterpriseImg from '../images/tile-enterprise.jpg';
import tilePerformanceImg from '../images/tile-performance.jpg';

const API = process.env.REACT_APP_API_URL || '';

const CAPABILITIES = [
    {
        id: 1,
        tag: 'AI & DATA',
        title: 'Intelligent Insights & Automation',
        desc: 'Turn data into actionable insights using AI, analytics, and automation to drive smarter business decisions.',
        image: capabilityAiImg
    },
    {
        id: 2,
        tag: 'CLOUD SERVICES',
        title: 'Scalable Cloud Architecture',
        desc: 'Design, migrate, and manage secure, scalable environments across AWS, Azure, and GCP.',
        image: capabilityCloudImg
    },
    {
        id: 3,
        tag: 'ENTERPRISE APPLICATIONS',
        title: 'SAP & Oracle ERP Solutions',
        desc: 'Deliver end-to-end SAP and Oracle ERP implementations, integrations, and support to streamline and modernize enterprise operations.',
        image: capabilityEnterpriseImg
    },
    {
        id: 4,
        tag: 'SERVICENOW',
        title: 'Digital Workflow Automation',
        desc: 'Automate workflows, improve service delivery, and enhance operational efficiency with ServiceNow solutions.',
        image: capabilityServicenowImg
    },
    {
        id: 5,
        tag: 'CYBER SECURITY',
        title: 'Zero Trust & Risk Protection',
        desc: 'Protect your business with advanced threat detection, identity security, and compliance-driven solutions.',
        image: capabilityCyberImg
    }
];


const Homepage = () => {
    const navigate = useNavigate();

    // Typewriter State
    const [text2, setText2] = useState('');
    const [showCursor2] = useState(true);
    const fullText2 = 'AI-Driven Digital Transformation';

    const capabilities = CAPABILITIES;
    const [testimonials, setTestimonials] = useState([]);
    const isLoading = false;

    useEffect(() => {
        fetch(`${API}/api/testimonials`)
            .then(res => res.ok ? res.json() : [])
            .then(data => setTestimonials(Array.isArray(data) ? data : []))
            .catch(() => {});
    }, []);

    const [activeIndex, setActiveIndex] = useState(0);
    const [featuredCapIndex, setFeaturedCapIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [activePhaseId, setActivePhaseId] = useState('discover');
    const [selectedTestimonial, setSelectedTestimonial] = useState(null);

    const phaseRefs = useRef({});

    const operatingPhases = useMemo(
        () => [
            {
                id: 'discover',
                title: 'Discover',
                desc: 'Understand your current systems, challenges, and business goals to define the right AI and cloud strategy.',
                outcome: 'Outcome: Clear roadmap aligned to business priorities.'
            },
            {
                id: 'build',
                title: 'Build',
                desc: 'Design and implement scalable cloud architectures and AI-driven solutions using modern engineering practices.',
                outcome: 'Outcome: Production-ready systems with measurable impact.'
            },
            {
                id: 'scale',
                title: 'Scale',
                desc: 'Optimize performance, automate operations, and continuously improve systems for long-term growth.',
                outcome: 'Outcome: Reliable, secure, and scalable enterprise platforms.'
            }
        ],
        []
    );

    const trustPoints = useMemo(
        () => [
            {
                title: 'Proven Delivery',
                detail: 'Consistent execution with clear milestones, accountability, and on-time delivery.'
            },
            {
                title: 'AI & Cloud Expertise and Enterprise Solutions',
                detail: 'Deep expertise in building scalable cloud platforms, production-ready AI and enterprise solutions.'
            },
            {
                title: 'Built for Scale',
                detail: 'Architectures designed for performance, security, and long-term growth.'
            }
        ],
        []
    );

    const trustCompanies = useMemo(
        () => ['FORTUNE 500', 'FINTECH', 'HEALTH SYSTEMS', 'LOGISTICS'],
        []
    );

    // Typewriter Effect
    useEffect(() => {
        let j = 0;
        const speed = 150;

        const typeLine2 = setInterval(() => {
            setText2(fullText2.slice(0, j + 1));
            j++;
            if (j === fullText2.length) {
                clearInterval(typeLine2);
            }
        }, speed);

        return () => {
            clearInterval(typeLine2);
        };
    }, []);

    // Auto-slide Effect for Capabilities
    useEffect(() => {
        if (capabilities.length <= 1) return;

        const interval = setInterval(() => {
            setFeaturedCapIndex((prevIndex) => (prevIndex + 1) % capabilities.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [capabilities.length]);

    // Auto-slide Effect for Testimonials
    useEffect(() => {
        if (testimonials.length <= 1 || isPaused) return;

        const interval = setInterval(() => {
            setActiveIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [testimonials.length, isPaused]);

    // Keep active index safe if testimonial count changes.
    useEffect(() => {
        if (testimonials.length === 0) {
            setActiveIndex(0);
            return;
        }

        if (activeIndex >= testimonials.length) {
            setActiveIndex(0);
        }
    }, [activeIndex, testimonials.length]);

    // Update active phase based on viewport visibility.
    useEffect(() => {
        const cards = Object.values(phaseRefs.current).filter(Boolean);
        if (!cards.length || typeof IntersectionObserver === 'undefined') return;

        const observer = new IntersectionObserver(
            (entries) => {
                const visibleEntries = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

                if (visibleEntries.length > 0) {
                    const nextActive = visibleEntries[0].target.getAttribute('data-phase-id');
                    if (nextActive) setActivePhaseId(nextActive);
                }
            },
            {
                root: null,
                rootMargin: '-35% 0px -45% 0px',
                threshold: [0.15, 0.35, 0.6]
            }
        );

        cards.forEach((card) => observer.observe(card));

        return () => observer.disconnect();
    }, [operatingPhases]);

    const featuredCapability = capabilities[featuredCapIndex] || null;
    const secondaryCapabilities = capabilities.filter((_, index) => index !== featuredCapIndex);
    const activeTestimonial = testimonials[activeIndex] || null;

    const handlePhaseJump = (phaseId) => {
        setActivePhaseId(phaseId);
        const targetNode = phaseRefs.current[phaseId];
        if (targetNode) {
            targetNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const openTestimonialPopup = (testimonial) => {
        setSelectedTestimonial(testimonial);
        document.body.style.overflow = 'hidden';
    };

    const closeTestimonialPopup = () => {
        setSelectedTestimonial(null);
        document.body.style.overflow = '';
    };

    const isLongTestimonial = (text) => text && text.length > 200;

    const truncateText = (text, maxLength = 200) => {
        if (!text || text.length <= maxLength) return text;
        return text.slice(0, maxLength).trim() + '...';
    };

    return (
        <div className="homepage-container">
            <div className="hero-section">
                <div className="hero-container home-section-shell">
                    <div className="hero-content">
                        <h1 className="hero-title">
                            Future-Proof Your Enterprise
                            <br />
                            <span className={`highlight-text ${showCursor2 ? 'typing-cursor' : ''}`}>{text2}</span>
                        </h1>

                        <p className="hero-command">
                            <span className="command-prefix">{'>'}</span> Driving AI-led innovation, cloud strategy and Enterprise Solutions...
                        </p>

                        <p className="hero-description">
                            We help organizations modernize their technology landscape with scalable cloud platforms, intelligent automation, and enterprise-grade security.
                        </p>

                        <a href="tel:+18322203283" className="btn btn-primary">Talk to an Expert</a>
                    </div>

                    <div className="hero-visual">
                        <div className="visual-glow"></div>
                        <div className="hero-metrics-panel">
                            <div className="hero-metrics-header">
                                <span className="hero-metrics-dot"></span>
                                <span className="hero-metrics-dot"></span>
                                <span className="hero-metrics-dot"></span>
                                <span className="hero-metrics-title">LIVE PERFORMANCE</span>
                            </div>
                            <div className="hero-metrics-grid">
                                <div className="hero-metric-item">
                                    <span className="hero-metric-num">5<span className="hero-metric-plus">+</span></span>
                                    <span className="hero-metric-lbl">Cloud Migration</span>
                                    <div className="hero-metric-bar"><div className="hero-metric-fill" style={{width:'55%'}}></div></div>
                                </div>
                               
                                <div className="hero-metric-item">
                                    <span className="hero-metric-num">100<span className="hero-metric-plus">%</span></span>
                                    <span className="hero-metric-lbl">Accelerated Enterprise Delivery</span>
                                    <div className="hero-metric-bar"><div className="hero-metric-fill" style={{width:'100%'}}></div></div>
                                </div>
                                <div className="hero-metric-item">
                                    <span className="hero-metric-num">99.9<span className="hero-metric-plus">%</span></span>
                                    <span className="hero-metric-lbl">High Availability Architecture</span>
                                    <div className="hero-metric-bar"><div className="hero-metric-fill" style={{width:'99%'}}></div></div>
                                </div>
                            </div>
                            <div className="hero-metrics-tags">
                                <span className="hero-metrics-tag">Data</span>
                                <span className="hero-metrics-tag">Cloud</span>
                                <span className="hero-metrics-tag">Security</span>
                                <span className="hero-metrics-tag">Oracle/SAP ERP</span>
                                <span className="hero-metrics-tag">ServiceNow</span>
                                <span className="hero-metrics-tag">Zero Trust</span>
                            </div>
                        </div>
                        <div className="hero-float-badge hero-float-badge-1">
                            <span className="hero-float-dot"></span> Zero Trust Security
                        </div>
                        <div className="hero-float-badge hero-float-badge-2">
                            <span className="hero-float-dot"></span> SAP &amp; Oracle ERP
                        </div>
                    </div>
                </div>

                <div className="scroll-to-explore">
                    <span>SCROLL TO EXPLORE</span>
                    <div className="scroll-line"></div>
                </div>
            </div>

            {/* Capability Command Grid */}
            <section id="capability-grid" className="capability-command-section">
                <div className="home-section-shell">
                    <div className="capability-command-header">
                        <span className="home-section-label light">CAPABILITY COMMAND GRID</span>
                        <h2>Precision architecture for high-stakes transformation.</h2>
                    </div>

                    {isLoading ? (
                        <div className="capability-loading-grid">
                            {Array.from({ length: 3 }).map((_, index) => (
                                <div key={`cap-loading-${index}`} className="capability-skeleton-card"></div>
                            ))}
                        </div>
                    ) : capabilities.length === 0 ? (
                        <div className="capability-empty-state">
                            <h3>Capabilities are being refreshed.</h3>
                            <p>Our current capability matrix will be published shortly. Explore services to see our active delivery streams.</p>
                            <button className="btn btn-primary" onClick={() => navigate('/services')}>EXPLORE SERVICES</button>
                        </div>
                    ) : (
                        <div className="capability-command-layout">
                            {featuredCapability && (
                                <article className="featured-capability-card">
                                    <div className="featured-capability-media">
                                        <img src={featuredCapability.image} alt={featuredCapability.title} />
                                        <div className="featured-capability-overlay"></div>
                                    </div>
                                    <div className="featured-capability-content">
                                        <span className="capability-tag">{featuredCapability.tag}</span>
                                        <h3>{featuredCapability.title}</h3>
                                        <p>{featuredCapability.desc}</p>
                                    </div>
                                </article>
                            )}

                            <div className={`secondary-capability-grid ${secondaryCapabilities.length === 1 ? 'single' : ''}`}>
                                {secondaryCapabilities.length > 0 ? (
                                    secondaryCapabilities.map((capability, index) => (
                                        <article key={`capability-mini-${capability.id || index}`} className="secondary-capability-card">
                                            <div className="secondary-capability-media">
                                                <img src={capability.image} alt={capability.title} />
                                                <div className="secondary-capability-overlay"></div>
                                            </div>
                                            <div className="secondary-capability-content">
                                                <span className="capability-tag">{capability.tag}</span>
                                                <h4>{capability.title}</h4>
                                                <p>{capability.desc}</p>
                                            </div>
                                        </article>
                                    ))
                                ) : (
                                    <article className="secondary-capability-card empty">
                                        <div className="secondary-capability-content">
                                            <span className="capability-tag">CONTINUOUS EXPANSION</span>
                                            <h4>Capability matrix in motion</h4>
                                            <p>Additional specialist modules are being deployed across cloud, security, and AI programs.</p>
                                        </div>
                                    </article>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* The Next Advantage Grid */}
            <section id="the-next" className="advantage-grid-section">
                <div className="home-section-shell">
                    <div className="advantage-header">
                        <h2 className="advantage-title">How We Deliver Results</h2>
                        <p className="advantage-subtitle">We combine AI, cloud, and enterprise expertise to help you modernize faster, scale efficiently, and reduce operational complexity</p>
                    </div>

                    <div className="advantage-layout">
                        <a href="/services" className="advantage-tile tile-experience">
                            <img src={tileAiImg} alt="AI in Production" />
                            <div className="tile-overlay">
                                <h3>AI in Production</h3>
                            </div>
                        </a>

                        <div className="advantage-subgrid">
                            <a href="/services" className="advantage-tile tile-insight">
                                <img src={tileCloudImg} alt="Cloud at Scale" />
                                <div className="tile-overlay">
                                    <h3>Cloud at Scale</h3>
                                </div>
                            </a>
                            <a href="/services" className="advantage-tile tile-innovate">
                                <img src={tileSecureImg} alt="Secure by Design" />
                                <div className="tile-overlay">
                                    <h3>Secure by Design</h3>
                                </div>
                            </a>
                            <a href="/services" className="advantage-tile tile-accelerate">
                                <img src={tileEnterpriseImg} alt="Enterprise Integration" />
                                <div className="tile-overlay">
                                    <h3>Enterprise Integration</h3>
                                </div>
                            </a>
                            <a href="/services" className="advantage-tile tile-assure">
                                <img src={tilePerformanceImg} alt="Performance & Reliability" />
                                <div className="tile-overlay">
                                    <h3>Performance & Reliability</h3>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Operating Playbook */}
            <section id="operating-playbook" className="operating-playbook-section">
                <div className="home-section-shell operating-playbook-layout">
                    <aside className="playbook-rail">
                        <span className="home-section-label">OPERATING PLAYBOOK</span>
                        <h2>Our Proven Delivery Approach</h2>
                        <p>A structured, outcome-driven approach to design, build, and scale enterprise AI and cloud solutions.</p>

                        <nav className="playbook-phase-nav" aria-label="Operating phases">
                            {operatingPhases.map((phase, index) => (
                                <button
                                    key={phase.id}
                                    type="button"
                                    className={`phase-nav-item ${activePhaseId === phase.id ? 'active' : ''}`}
                                    onClick={() => handlePhaseJump(phase.id)}
                                >
                                    <span className="phase-nav-index">{`0${index + 1}`}</span>
                                    <span>{phase.title}</span>
                                </button>
                            ))}
                        </nav>
                    </aside>

                    <div className="playbook-phase-stack">
                        {operatingPhases.map((phase, index) => (
                            <article
                                key={phase.id}
                                ref={(node) => {
                                    phaseRefs.current[phase.id] = node;
                                }}
                                data-phase-id={phase.id}
                                className={`playbook-phase-card ${activePhaseId === phase.id ? 'active' : ''}`}
                            >
                                <span className="phase-card-kicker">{`Phase 0${index + 1}`}</span>
                                <h3>{phase.title}</h3>
                                <p>{phase.desc}</p>
                                <strong>{phase.outcome}</strong>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Executive Action + Trust + Testimonial — unified section */}
            <section id="executive-cta" className="executive-cta-section">
                <div className="home-section-shell">
                    <div className="executive-cta-card">

                        {/* Top: two-column — Executive Action left, Trust panel right */}
                        <div className="cta-trust-grid">
                            <div className="cta-left">
                                <div className="executive-cta-copy">
                                    <span className="home-section-label light">EXECUTIVE ACTION</span>
                                    <h2>Ready to define the next decade?</h2>
                                    <p>Align with our advisory and engineering leadership team to accelerate your transformation roadmap.</p>
                                </div>
                                <div className="executive-cta-actions cta-left-actions">
                                    <button className="btn btn-primary" onClick={() => navigate('/contact')}>INITIATE INQUIRY</button>
                                    <button className="btn btn-secondary-home" onClick={() => navigate('/services')}>
                                        EXPLORE SERVICES <ArrowRightIcon className="arrow-small" />
                                    </button>
                                </div>
                                <p className="executive-cta-confidence">Trusted by operators navigating critical infrastructure at enterprise scale.</p>
                            </div>

                            <div className="cta-right">
                                <h3>Trusted by Teams Delivering Real Results</h3>
                                <ul>
                                    {trustPoints.map((point) => (
                                        <li key={point.title}>
                                            <CheckIcon className="trust-check-icon" />
                                            <div>
                                                <strong>{point.title}</strong>
                                                <p>{point.detail}</p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                                <div className="trust-company-strip" aria-label="Industries served">
                                    {trustCompanies.map((company) => (
                                        <span key={company}>{company}</span>
                                    ))}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* Proof in Motion — separate section */}
            <section id="testimonials" className="executive-cta-section">
                <div className="home-section-shell">
                    <div className="executive-cta-card">
                        <div className="testimonial-full">
                            <div className="testimonial-full-header">
                                <span className="home-section-label light">PROOF IN MOTION</span>
                                <h3>Trust, validated by operators in the Proven Customer Testimonials.</h3>
                            </div>

                            {activeTestimonial ? (
                                <div className="proof-testimonial-card">
                                    <span className="proof-quote-mark">❝</span>
                                    <p className="proof-quote-text">
                                        {isLongTestimonial(activeTestimonial.message) ? (
                                            <>
                                                {truncateText(activeTestimonial.message)}
                                                <button
                                                    className="read-more-btn"
                                                    onClick={() => openTestimonialPopup(activeTestimonial)}
                                                >
                                                    Read more
                                                </button>
                                            </>
                                        ) : (
                                            activeTestimonial.message
                                        )}
                                    </p>
                                    <div className="proof-author-block">
                                        {activeTestimonial.avatarUrl && (
                                            <img
                                                src={activeTestimonial.avatarUrl}
                                                alt={activeTestimonial.clientName}
                                                className="proof-author-avatar"
                                            />
                                        )}
                                        <div>
                                            <strong>{activeTestimonial.clientName}</strong>
                                            <span>{activeTestimonial.designation}, {activeTestimonial.company}</span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="proof-testimonial-card proof-empty-card">
                                    <h3>Client proof points are being curated.</h3>
                                    <p>Our latest enterprise testimonials will be available shortly.</p>
                                </div>
                            )}

                            {testimonials.length > 1 && (
                                <div className="proof-controls">
                                    <div className="proof-dots" role="tablist" aria-label="Testimonial carousel controls">
                                        {testimonials.map((testimonial, index) => (
                                            <button
                                                key={testimonial.id || index}
                                                type="button"
                                                role="tab"
                                                aria-selected={activeIndex === index}
                                                aria-label={`Show testimonial ${index + 1}`}
                                                className={`proof-dot ${activeIndex === index ? 'active' : ''}`}
                                                onClick={() => setActiveIndex(index)}
                                            ></button>
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        className="proof-toggle-btn"
                                        aria-label={isPaused ? 'Resume autoplay' : 'Pause autoplay'}
                                        onClick={() => setIsPaused((prev) => !prev)}
                                    >
                                        {isPaused ? <PlayIcon /> : <PauseIcon />}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonial Popup Modal */}
            {selectedTestimonial && (
                <div className="testimonial-popup-overlay" onClick={closeTestimonialPopup}>
                    <div className="testimonial-popup-content" onClick={(e) => e.stopPropagation()}>
                        <button className="testimonial-popup-close" onClick={closeTestimonialPopup} aria-label="Close popup">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                        <span className="proof-quote-mark popup-quote-mark">❝</span>
                        <p className="proof-quote-text popup-quote-text">{selectedTestimonial.message}</p>
                        <div className="proof-author-block popup-author-block">
                            {selectedTestimonial.avatarUrl && (
                                <img
                                    src={selectedTestimonial.avatarUrl}
                                    alt={selectedTestimonial.clientName}
                                    className="proof-author-avatar"
                                />
                            )}
                            <div>
                                <strong>{selectedTestimonial.clientName}</strong>
                                <span>{selectedTestimonial.designation}, {selectedTestimonial.company}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Homepage;
